"use client"

import { useState, useEffect, useRef, useCallback } from "react"
import { X, Loader2, Plus, ChevronDown } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useToast } from "@/hooks/use-toast"
import { cn } from "@/lib/utils"
import { CRM_API_BASE } from "@/lib/config"
import { httpJson } from "@/lib/http"

const ORG_SEARCH_PAGE_SIZE = 10
const EVENT_TYPE_PAGE_SIZE = 100

function buildEventTypeListUrl(offset: number): string {
  const q = new URLSearchParams({
    limit: String(EVENT_TYPE_PAGE_SIZE),
    offset: String(offset),
    ordering: "name",
  })
  return `${CRM_API_BASE}/event_type/?${q.toString()}`
}

const OPPORTUNITY_TYPES = [
  { value: "event", label: "Event" },
  { value: "general_donation", label: "General donation" },
  { value: "organization_membership", label: "Organization membership" },
  { value: "chamber_of_commerce_membership", label: "Chamber of Commerce membership" },
] as const

type OppTypeValue = (typeof OPPORTUNITY_TYPES)[number]["value"]

type OppFieldErrors = Partial<{
  organization: string
  name: string
  opportunityType: string
  eventDate: string
  eventTypeId: string
  city: string
  state: string
  thankYouUrl: string
  sponsorUrl: string
}>

const OPP_FIELD_SCROLL_ORDER: (keyof OppFieldErrors)[] = [
  "organization",
  "name",
  "opportunityType",
  "eventDate",
  "eventTypeId",
  "city",
  "state",
  "thankYouUrl",
  "sponsorUrl",
]

const OPP_FIELD_SCROLL_ID: Record<keyof OppFieldErrors, string> = {
  organization: "opp-org-field",
  name: "opp-name",
  opportunityType: "opp-opportunity-type",
  eventDate: "opp-event-date",
  eventTypeId: "opp-event-type",
  city: "opp-city",
  state: "opp-state",
  thankYouUrl: "opp-thank-you",
  sponsorUrl: "opp-sponsor-url",
}

interface OrgListItem {
  id: number
  name: string
}

interface EventTypeListItem {
  id: number
  name: string
}

export interface AddOpportunityModalProps {
  open: boolean
  onClose: () => void
  onSuccess?: (opportunityId: number) => void
  lockedOrganizationId?: number
  lockedOrganizationName?: string
}

function isValidHttpUrl(value: string): boolean {
  const v = value.trim()
  if (!v) return false
  try {
    const u = new URL(v.startsWith("http") ? v : `https://${v}`)
    return u.protocol === "http:" || u.protocol === "https:"
  } catch {
    return false
  }
}

function normalizeUrl(value: string): string {
  const v = value.trim()
  if (!v) return ""
  return v.startsWith("http://") || v.startsWith("https://") ? v : `https://${v}`
}

function nowIso(): string {
  return new Date().toISOString()
}

function eventDateToIso(dateYmd: string): string {
  const t = dateYmd.trim()
  if (!t) return ""
  return new Date(`${t}T12:00:00.000Z`).toISOString()
}

function mapEventTypeResults(raw: unknown[]): EventTypeListItem[] {
  const out: EventTypeListItem[] = []
  for (const item of raw) {
    if (!item || typeof item !== "object") continue
    const row = item as { id?: unknown; name?: unknown }
    const id =
      typeof row.id === "number"
        ? row.id
        : typeof row.id === "string"
          ? Number.parseInt(row.id, 10)
          : NaN
    if (!Number.isFinite(id)) continue
    const name = typeof row.name === "string" ? row.name.trim() : ""
    out.push({ id, name: name || `Type #${id}` })
  }
  return out
}

function parseResourceId(body: unknown, resourceLabel: string): number {
  if (body && typeof body === "object" && "data" in body) {
    const data = (body as { data?: { id?: unknown } }).data
    const id = data?.id
    if (typeof id === "number" && !Number.isNaN(id)) return id
    if (typeof id === "string") {
      const n = Number.parseInt(id, 10)
      if (!Number.isNaN(n)) return n
    }
  }
  if (body && typeof body === "object" && "id" in body) {
    const id = (body as { id?: unknown }).id
    if (typeof id === "number" && !Number.isNaN(id)) return id
    if (typeof id === "string") {
      const n = Number.parseInt(id, 10)
      if (!Number.isNaN(n)) return n
    }
  }
  throw new Error(`Unexpected response when creating ${resourceLabel}`)
}

function buildOppCreatePayload(params: {
  orgId: number
  name: string
  type: OppTypeValue
  isEvent: boolean
  eventDateIso: string | null
  eventTypeId: number | null
  thankYouUrl: string
  sponsorUrl: string
}): Record<string, unknown> {
  const ts = nowIso()
  const payload: Record<string, unknown> = {
    org: params.orgId,
    name: params.name.trim(),
    type: params.type,
    date_added: ts,
    deleted: 0,
    domain_authority: 0,
    alexa_rank: 0,
    links_in_count: 0,
    is_event: params.isEvent ? 1 : 0,
    is_active: true,
  }

  if (params.isEvent && params.eventDateIso) {
    payload.event_date = params.eventDateIso
  }
  if (params.isEvent && params.eventTypeId != null) {
    payload.event_type = params.eventTypeId
  }

  const thankTrim = params.thankYouUrl.trim()
  if (thankTrim) {
    const thankNorm = normalizeUrl(thankTrim)
    payload.url = thankNorm
    payload.thank_you_url = thankNorm
  }

  if (params.sponsorUrl.trim()) {
    payload.sponsor_url = normalizeUrl(params.sponsorUrl)
  }

  return payload
}

export function AddOpportunityModal({
  open,
  onClose,
  onSuccess,
  lockedOrganizationId,
  lockedOrganizationName,
}: AddOpportunityModalProps) {
  const { toast } = useToast()
  const [loading, setLoading] = useState(false)
  const [fieldErrors, setFieldErrors] = useState<OppFieldErrors>({})

  const [name, setName] = useState("")
  const [opportunityType, setOpportunityType] = useState<OppTypeValue | "">("")
  const [eventDate, setEventDate] = useState("")
  const [eventTypeId, setEventTypeId] = useState<number | null>(null)
  const [eventTypes, setEventTypes] = useState<EventTypeListItem[]>([])
  const [eventTypesLoading, setEventTypesLoading] = useState(false)
  const [eventTypesLoadingMore, setEventTypesLoadingMore] = useState(false)
  const [eventTypesHasMore, setEventTypesHasMore] = useState(false)
  const [showEventTypeDropdown, setShowEventTypeDropdown] = useState(false)
  const [addressLine1, setAddressLine1] = useState("")
  const [addressLine2, setAddressLine2] = useState("")
  const [city, setCity] = useState("")
  const [state, setState] = useState("")
  const [zip, setZip] = useState("")
  const [thankYouUrl, setThankYouUrl] = useState("")
  const [sponsorUrl, setSponsorUrl] = useState("")

  const [orgSearch, setOrgSearch] = useState("")
  const [initialOrgs, setInitialOrgs] = useState<OrgListItem[]>([])
  const [initialOrgsLoading, setInitialOrgsLoading] = useState(false)
  const [orgResults, setOrgResults] = useState<OrgListItem[]>([])
  const [orgsLoading, setOrgsLoading] = useState(false)
  const [orgsLoadingMore, setOrgsLoadingMore] = useState(false)
  const [orgSearchHasMore, setOrgSearchHasMore] = useState(false)
  const [selectedOrg, setSelectedOrg] = useState<OrgListItem | null>(null)
  const [showOrgDropdown, setShowOrgDropdown] = useState(false)
  const orgAbortRef = useRef<AbortController | null>(null)
  const orgLoadMoreAbortRef = useRef<AbortController | null>(null)
  const orgSearchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const orgComboRef = useRef<HTMLDivElement>(null)
  const orgResultsRef = useRef<OrgListItem[]>([])
  const orgSearchTotalRef = useRef(0)
  const orgSearchInputRef = useRef("")
  const eventTypeComboRef = useRef<HTMLDivElement>(null)
  const eventTypesListRef = useRef<EventTypeListItem[]>([])
  const eventTypeTotalRef = useRef(0)
  const eventTypeLoadMoreAbortRef = useRef<AbortController | null>(null)

  const isOrgLocked = typeof lockedOrganizationId === "number"

  const effectiveOrgId = isOrgLocked ? lockedOrganizationId : selectedOrg?.id
  const effectiveOrgName = isOrgLocked ? (lockedOrganizationName ?? "") : (selectedOrg?.name ?? "")

  useEffect(() => {
    orgSearchInputRef.current = orgSearch
  }, [orgSearch])

  useEffect(() => {
    orgResultsRef.current = orgResults
  }, [orgResults])

  const resetForm = useCallback(() => {
    setName("")
    setOpportunityType("")
    setEventDate("")
    setEventTypeId(null)
    setEventTypes([])
    setEventTypesLoading(false)
    setEventTypesLoadingMore(false)
    setEventTypesHasMore(false)
    setShowEventTypeDropdown(false)
    eventTypesListRef.current = []
    eventTypeTotalRef.current = 0
    if (eventTypeLoadMoreAbortRef.current) {
      eventTypeLoadMoreAbortRef.current.abort()
      eventTypeLoadMoreAbortRef.current = null
    }
    setAddressLine1("")
    setAddressLine2("")
    setCity("")
    setState("")
    setZip("")
    setThankYouUrl("")
    setSponsorUrl("")
    setOrgSearch("")
    setInitialOrgs([])
    setInitialOrgsLoading(false)
    setOrgResults([])
    setOrgSearchHasMore(false)
    setOrgsLoadingMore(false)
    orgSearchTotalRef.current = 0
    orgResultsRef.current = []
    setSelectedOrg(null)
    setShowOrgDropdown(false)
    setFieldErrors({})
  }, [])

  useEffect(() => {
    if (!open) {
      resetForm()
    }
  }, [open, resetForm])

  useEffect(() => {
    if (!open || isOrgLocked) return
    let cancelled = false
    setInitialOrgsLoading(true)
    httpJson<{ results?: OrgListItem[] }>(`${CRM_API_BASE}/org/?limit=10`)
      .then((res) => {
        if (!cancelled) setInitialOrgs(Array.isArray(res.results) ? res.results : [])
      })
      .catch(() => {
        if (!cancelled) setInitialOrgs([])
      })
      .finally(() => {
        if (!cancelled) setInitialOrgsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, isOrgLocked])

  useEffect(() => {
    if (!open) return
    let cancelled = false
    if (eventTypeLoadMoreAbortRef.current) {
      eventTypeLoadMoreAbortRef.current.abort()
      eventTypeLoadMoreAbortRef.current = null
    }
    setEventTypesLoading(true)
    setEventTypesHasMore(false)
    setEventTypesLoadingMore(false)
    eventTypesListRef.current = []
    eventTypeTotalRef.current = 0
    setEventTypes([])
    httpJson<{ results?: unknown[]; count?: number }>(buildEventTypeListUrl(0))
      .then((res) => {
        if (cancelled) return
        const raw = Array.isArray(res.results) ? res.results : []
        const mapped = mapEventTypeResults(raw)
        const total = typeof res.count === "number" ? res.count : mapped.length
        eventTypeTotalRef.current = total
        eventTypesListRef.current = mapped
        setEventTypes(mapped)
        setEventTypesHasMore(mapped.length < total)
      })
      .catch(() => {
        if (!cancelled) {
          setEventTypes([])
          setEventTypesHasMore(false)
          eventTypesListRef.current = []
          eventTypeTotalRef.current = 0
        }
      })
      .finally(() => {
        if (!cancelled) setEventTypesLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open])

  useEffect(() => {
    if (!showEventTypeDropdown) return
    const onPointerDown = (e: PointerEvent) => {
      const el = eventTypeComboRef.current
      if (el && !el.contains(e.target as Node)) {
        setShowEventTypeDropdown(false)
      }
    }
    document.addEventListener("pointerdown", onPointerDown, true)
    return () => document.removeEventListener("pointerdown", onPointerDown, true)
  }, [showEventTypeDropdown])

  useEffect(() => {
    if (!open || isOrgLocked) return

    if (orgSearchDebounceRef.current) clearTimeout(orgSearchDebounceRef.current)

    const q = orgSearch.trim()
    if (q.length < 2) {
      if (orgAbortRef.current) {
        orgAbortRef.current.abort()
        orgAbortRef.current = null
      }
      if (orgLoadMoreAbortRef.current) {
        orgLoadMoreAbortRef.current.abort()
        orgLoadMoreAbortRef.current = null
      }
      setOrgResults([])
      setOrgSearchHasMore(false)
      setOrgsLoading(false)
      setOrgsLoadingMore(false)
      orgSearchTotalRef.current = 0
      return
    }

    orgSearchDebounceRef.current = setTimeout(() => {
      if (orgAbortRef.current) orgAbortRef.current.abort()
      if (orgLoadMoreAbortRef.current) {
        orgLoadMoreAbortRef.current.abort()
        orgLoadMoreAbortRef.current = null
      }
      const ac = new AbortController()
      orgAbortRef.current = ac
      setOrgsLoading(true)
      setOrgSearchHasMore(false)
      httpJson<{ results?: OrgListItem[]; count?: number }>(
        `${CRM_API_BASE}/org/?q=${encodeURIComponent(q)}&limit=${ORG_SEARCH_PAGE_SIZE}&offset=0`,
        { signal: ac.signal },
      )
        .then((res) => {
          if (orgSearchInputRef.current.trim() !== q) return
          const batch = Array.isArray(res.results) ? res.results : []
          const total = typeof res.count === "number" ? res.count : batch.length
          orgSearchTotalRef.current = total
          orgResultsRef.current = batch
          setOrgResults(batch)
          setOrgSearchHasMore(batch.length < total)
        })
        .catch((err) => {
          if (err instanceof Error && err.name === "AbortError") return
          setOrgResults([])
          setOrgSearchHasMore(false)
          orgSearchTotalRef.current = 0
        })
        .finally(() => setOrgsLoading(false))
    }, 300)

    return () => {
      if (orgSearchDebounceRef.current) clearTimeout(orgSearchDebounceRef.current)
    }
  }, [orgSearch, open, isOrgLocked])

  useEffect(() => {
    if (!showOrgDropdown) return
    const onPointerDown = (e: PointerEvent) => {
      const el = orgComboRef.current
      if (el && !el.contains(e.target as Node)) {
        setShowOrgDropdown(false)
      }
    }
    document.addEventListener("pointerdown", onPointerDown, true)
    return () => document.removeEventListener("pointerdown", onPointerDown, true)
  }, [showOrgDropdown])

  const handleOrgSearchScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const q = orgSearch.trim()
      if (q.length < 2) return
      if (!orgSearchHasMore || orgsLoading || orgsLoadingMore) return
      const el = e.currentTarget
      const threshold = 72
      if (el.scrollHeight - el.scrollTop - el.clientHeight > threshold) return

      const offset = orgResultsRef.current.length
      const total = orgSearchTotalRef.current
      if (offset >= total) {
        setOrgSearchHasMore(false)
        return
      }

      if (orgLoadMoreAbortRef.current) orgLoadMoreAbortRef.current.abort()
      const ac = new AbortController()
      orgLoadMoreAbortRef.current = ac
      setOrgsLoadingMore(true)

      httpJson<{ results?: OrgListItem[]; count?: number }>(
        `${CRM_API_BASE}/org/?q=${encodeURIComponent(q)}&limit=${ORG_SEARCH_PAGE_SIZE}&offset=${offset}`,
        { signal: ac.signal },
      )
        .then((res) => {
          if (orgSearchInputRef.current.trim() !== q) return
          const batch = Array.isArray(res.results) ? res.results : []
          const countTotal = typeof res.count === "number" ? res.count : orgSearchTotalRef.current
          orgSearchTotalRef.current = countTotal
          const prev = orgResultsRef.current
          const seen = new Set(prev.map((o) => o.id))
          const next = [...prev]
          for (const o of batch) {
            if (!seen.has(o.id)) {
              seen.add(o.id)
              next.push(o)
            }
          }
          orgResultsRef.current = next
          setOrgResults(next)
          setOrgSearchHasMore(next.length < countTotal && batch.length > 0)
        })
        .catch((err) => {
          if (err instanceof Error && err.name === "AbortError") return
        })
        .finally(() => setOrgsLoadingMore(false))
    },
    [orgSearch, orgSearchHasMore, orgsLoading, orgsLoadingMore],
  )

  const pickOrg = (org: OrgListItem) => {
    setSelectedOrg(org)
    setOrgSearch("")
    setOrgResults([])
    setShowOrgDropdown(false)
    setFieldErrors((prev) => ({ ...prev, organization: undefined }))
  }

  const handleEventTypeScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      if (!eventTypesHasMore || eventTypesLoading || eventTypesLoadingMore) return
      const el = e.currentTarget
      const threshold = 72
      if (el.scrollHeight - el.scrollTop - el.clientHeight > threshold) return

      const offset = eventTypesListRef.current.length
      const total = eventTypeTotalRef.current
      if (offset >= total) {
        setEventTypesHasMore(false)
        return
      }

      if (eventTypeLoadMoreAbortRef.current) eventTypeLoadMoreAbortRef.current.abort()
      const ac = new AbortController()
      eventTypeLoadMoreAbortRef.current = ac
      setEventTypesLoadingMore(true)

      httpJson<{ results?: unknown[]; count?: number }>(buildEventTypeListUrl(offset), {
        signal: ac.signal,
      })
        .then((res) => {
          const batchRaw = Array.isArray(res.results) ? res.results : []
          const batch = mapEventTypeResults(batchRaw)
          const countTotal = typeof res.count === "number" ? res.count : eventTypeTotalRef.current
          eventTypeTotalRef.current = countTotal
          const prev = eventTypesListRef.current
          const seen = new Set(prev.map((t) => t.id))
          const next = [...prev]
          for (const t of batch) {
            if (!seen.has(t.id)) {
              seen.add(t.id)
              next.push(t)
            }
          }
          eventTypesListRef.current = next
          setEventTypes(next)
          setEventTypesHasMore(next.length < countTotal && batch.length > 0)
        })
        .catch((err) => {
          if (err instanceof Error && err.name === "AbortError") return
        })
        .finally(() => setEventTypesLoadingMore(false))
    },
    [eventTypesHasMore, eventTypesLoading, eventTypesLoadingMore],
  )

  const pickEventType = (et: EventTypeListItem) => {
    setEventTypeId(et.id)
    setShowEventTypeDropdown(false)
    setFieldErrors((prev) => ({ ...prev, eventTypeId: undefined }))
  }

  const scrollToFirstOppFieldError = useCallback((errors: OppFieldErrors) => {
    for (const key of OPP_FIELD_SCROLL_ORDER) {
      if (!errors[key]) continue
      const id = OPP_FIELD_SCROLL_ID[key]
      const el = typeof document !== "undefined" ? document.getElementById(id) : null
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" })
        break
      }
    }
  }, [])

  const createAddress = async (): Promise<number> => {
    const addrRaw = await httpJson<unknown>(`${CRM_API_BASE}/address/`, {
      method: "POST",
      body: JSON.stringify({
        line_1: addressLine1.trim() || null,
        line_2: addressLine2.trim() || null,
        reference: null,
        city: city.trim() || null,
        state: state.trim() || null,
        zip_code: zip.trim() || null,
        tags: ["primary"],
      }),
    })
    return parseResourceId(addrRaw, "address")
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (loading) return

    const errors: OppFieldErrors = {}

    const orgId = effectiveOrgId
    if (!isOrgLocked && (orgId == null || !selectedOrg)) {
      errors.organization = "Select an organization."
    }

    if (!name.trim()) {
      errors.name = "Opportunity name is required."
    }

    if (!opportunityType) {
      errors.opportunityType = "Select an opportunity type."
    }

    const isEvent = opportunityType === "event"
    let eventDateIso: string | null = null
    if (isEvent) {
      if (!eventDate.trim()) {
        errors.eventDate = "Event date is required for event opportunities."
      } else {
        eventDateIso = eventDateToIso(eventDate)
      }
      if (eventTypeId == null) {
        errors.eventTypeId = "Select an event type."
      }
    }

    if (!city.trim()) {
      errors.city = "City is required."
    }
    if (!state.trim()) {
      errors.state = "State is required."
    }

    const thankTrim = thankYouUrl.trim()
    if (!thankTrim) {
      errors.thankYouUrl = "Source URL is required."
    } else if (!isValidHttpUrl(thankYouUrl)) {
      errors.thankYouUrl = "Enter a valid URL."
    }

    if (sponsorUrl.trim() && !isValidHttpUrl(sponsorUrl)) {
      errors.sponsorUrl = "Enter a valid URL or leave this field blank."
    }

    const hasErrors = Object.keys(errors).length > 0
    if (hasErrors) {
      setFieldErrors(errors)
      requestAnimationFrame(() => scrollToFirstOppFieldError(errors))
      return
    }

    setFieldErrors({})

    const oppPayload = buildOppCreatePayload({
      orgId: orgId!,
      name: name.trim(),
      type: opportunityType as OppTypeValue,
      isEvent,
      eventDateIso,
      eventTypeId: isEvent ? eventTypeId! : null,
      thankYouUrl,
      sponsorUrl,
    })

    setLoading(true)
    try {
      const addressId = await createAddress()

      const rawCreate = await httpJson<unknown>(`${CRM_API_BASE}/opp/`, {
        method: "POST",
        body: JSON.stringify(oppPayload),
      })

      const oppId = parseResourceId(rawCreate, "opportunity")

      await httpJson(`${CRM_API_BASE}/opp/${oppId}/`, {
        method: "PATCH",
        body: JSON.stringify({ addresses: [addressId], date_updated: nowIso() }),
      })

      toast({ title: "Success", description: "Opportunity created successfully." })
      onSuccess?.(oppId)
      onClose()
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to create opportunity"
      toast({ title: "Cannot create opportunity", description: message, variant: "destructive" })
    } finally {
      setLoading(false)
    }
  }

  const labelClass = "mb-1.5 block text-sm font-medium text-foreground"

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-3xl w-[95vw] max-h-[90vh] overflow-y-auto p-0 gap-0 rounded-lg border border-gray-300 shadow-xl [&>button]:hidden"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader className="sticky top-0 z-10 shrink-0 border-b border-gray-300 bg-white px-6 py-4 flex flex-row items-center justify-between space-y-0">
          <DialogTitle className="text-xl font-semibold text-gray-900">Add opportunity</DialogTitle>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-700 -m-1.5"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-6 p-6">
          <div id="opp-org-field">
            <Label className={labelClass}>
              Organization <span className="text-red-500">*</span>
            </Label>
            {isOrgLocked ? (
              <Input value={effectiveOrgName} disabled className="bg-muted" />
            ) : (
              <div className="space-y-2 relative">
                {selectedOrg ? (
                  <div
                    className={cn(
                      "flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2 text-sm",
                      fieldErrors.organization ? "border-red-500" : "border-input",
                    )}
                  >
                    <span className="flex-1 truncate">{selectedOrg.name}</span>
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-foreground text-xs underline"
                      onClick={() => {
                        setSelectedOrg(null)
                        setFieldErrors((prev) => ({ ...prev, organization: undefined }))
                      }}
                    >
                      Change
                    </button>
                  </div>
                ) : (
                  <div ref={orgComboRef} className="relative">
                    <Input
                      id="opp-org-search"
                      placeholder="Search organizations…"
                      value={orgSearch}
                      className={cn("pr-10", fieldErrors.organization && "border-red-500 focus:border-red-500")}
                      onChange={(e) => {
                        setOrgSearch(e.target.value)
                        setShowOrgDropdown(true)
                        setFieldErrors((prev) => ({ ...prev, organization: undefined }))
                      }}
                      onFocus={() => setShowOrgDropdown(true)}
                      autoComplete="off"
                      aria-invalid={!!fieldErrors.organization}
                    />
                    <button
                      type="button"
                      aria-expanded={showOrgDropdown}
                      aria-controls="opp-org-listbox"
                      aria-label={showOrgDropdown ? "Collapse organization list" : "Expand organization list"}
                      className="absolute right-2 top-1/2 z-10 -translate-y-1/2 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => setShowOrgDropdown((open) => !open)}
                    >
                      <ChevronDown
                        className={`h-4 w-4 transition-transform duration-200 ${showOrgDropdown ? "rotate-180" : ""}`}
                        aria-hidden
                      />
                    </button>
                    {showOrgDropdown && (
                      <div
                        id="opp-org-listbox"
                        role="listbox"
                        onScroll={handleOrgSearchScroll}
                        className="absolute z-[9999] mt-1 max-h-60 w-full overflow-y-auto rounded-md border border-gray-300 bg-white shadow-xl"
                      >
                        {(() => {
                          const q = orgSearch.trim()
                          if (q.length === 1) {
                            return (
                              <div className="p-3 text-sm text-gray-600">
                                Type at least 2 characters to search...
                              </div>
                            )
                          }
                          if (q.length >= 2) {
                            if (orgsLoading) {
                              return (
                                <div className="flex items-center gap-2 p-3 text-sm text-muted-foreground">
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                  Searching…
                                </div>
                              )
                            }
                            if (orgResults.length === 0) {
                              return (
                                <div className="p-3 text-sm text-muted-foreground">
                                  No organizations found for &quot;{q}&quot;
                                </div>
                              )
                            }
                            return (
                              <>
                                {orgResults.map((o) => (
                                  <button
                                    key={o.id}
                                    type="button"
                                    className="w-full border-b border-gray-100 px-4 py-3 text-left text-sm text-gray-900 last:border-b-0 hover:bg-blue-50"
                                    onClick={() => pickOrg(o)}
                                  >
                                    {o.name}
                                  </button>
                                ))}
                                {orgsLoadingMore && (
                                  <div className="flex items-center gap-2 border-t border-gray-100 px-4 py-3 text-sm text-muted-foreground">
                                    <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                                    Loading more…
                                  </div>
                                )}
                              </>
                            )
                          }
                          if (initialOrgsLoading) {
                            return (
                              <div className="flex items-center gap-2 p-3 text-sm text-muted-foreground">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Loading organizations…
                              </div>
                            )
                          }
                          return (
                            <>
                              {initialOrgs.length === 0 ? (
                                <div className="p-3 text-sm text-muted-foreground">No organizations available</div>
                              ) : (
                                initialOrgs.map((o) => (
                                  <button
                                    key={o.id}
                                    type="button"
                                    className="w-full border-b border-gray-100 px-4 py-3 text-left text-sm text-gray-900 hover:bg-blue-50"
                                    onClick={() => pickOrg(o)}
                                  >
                                    {o.name}
                                  </button>
                                ))
                              )}
                              <div className="border-t border-gray-200 p-3 text-sm text-gray-600">
                                Type at least 2 characters to search...
                              </div>
                            </>
                          )
                        })()}
                      </div>
                    )}
                  </div>
                )}
                {fieldErrors.organization && (
                  <p className="text-sm text-red-600" role="alert">
                    {fieldErrors.organization}
                  </p>
                )}
              </div>
            )}
          </div>

          <div>
            <Label htmlFor="opp-name" className={labelClass}>
              Opportunity name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="opp-name"
              value={name}
              onChange={(e) => {
                setName(e.target.value)
                setFieldErrors((prev) => ({ ...prev, name: undefined }))
              }}
              placeholder="Enter opportunity name"
              className={cn(fieldErrors.name && "border-red-500 focus:border-red-500")}
              aria-invalid={!!fieldErrors.name}
            />
            {fieldErrors.name && (
              <p className="mt-1 text-sm text-red-600" role="alert">
                {fieldErrors.name}
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="opp-opportunity-type" className={labelClass}>
              Opportunity type <span className="text-red-500">*</span>
            </Label>
            <Select
              value={opportunityType || undefined}
              onValueChange={(v) => {
                const next = v as OppTypeValue
                setOpportunityType(next)
                if (next !== "event") {
                  setEventDate("")
                  setEventTypeId(null)
                  setShowEventTypeDropdown(false)
                }
                setFieldErrors((prev) => ({
                  ...prev,
                  opportunityType: undefined,
                  ...(next !== "event" ? { eventDate: undefined, eventTypeId: undefined } : {}),
                }))
              }}
            >
              <SelectTrigger
                id="opp-opportunity-type"
                className={cn(
                  "border-gray-400 bg-background focus:border-primary focus:ring-0 focus:ring-offset-0",
                  fieldErrors.opportunityType && "border-red-500 focus:border-red-500",
                )}
                aria-invalid={!!fieldErrors.opportunityType}
              >
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {OPPORTUNITY_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {fieldErrors.opportunityType && (
              <p className="mt-1 text-sm text-red-600" role="alert">
                {fieldErrors.opportunityType}
              </p>
            )}
          </div>

          {opportunityType === "event" && (
            <>
              <div>
                <Label htmlFor="opp-event-date" className={labelClass}>
                  Event date <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="opp-event-date"
                  type="date"
                  value={eventDate}
                  onChange={(e) => {
                    setEventDate(e.target.value)
                    setFieldErrors((prev) => ({ ...prev, eventDate: undefined }))
                  }}
                  className={cn(fieldErrors.eventDate && "border-red-500 focus:border-red-500")}
                  aria-invalid={!!fieldErrors.eventDate}
                />
                {fieldErrors.eventDate && (
                  <p className="mt-1 text-sm text-red-600" role="alert">
                    {fieldErrors.eventDate}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="opp-event-type-trigger" className={labelClass}>
                  Event type <span className="text-red-500">*</span>
                </Label>
                <div ref={eventTypeComboRef} id="opp-event-type" className="relative">
                  <button
                    type="button"
                    id="opp-event-type-trigger"
                    disabled={eventTypesLoading}
                    aria-expanded={showEventTypeDropdown}
                    aria-haspopup="listbox"
                    aria-controls="opp-event-type-listbox"
                    aria-invalid={!!fieldErrors.eventTypeId}
                    className={cn(
                      "flex h-10 w-full items-center justify-between rounded-md border border-gray-400 bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1",
                      fieldErrors.eventTypeId && "border-red-500 focus:border-red-500",
                    )}
                    onClick={() => {
                      if (eventTypesLoading) return
                      setShowEventTypeDropdown((open) => !open)
                    }}
                  >
                    <span className="text-left truncate text-foreground">
                      {eventTypesLoading
                        ? "Loading event types…"
                        : eventTypeId != null
                          ? (eventTypes.find((t) => t.id === eventTypeId)?.name ?? `Type #${eventTypeId}`)
                          : "Select event type"}
                    </span>
                    <ChevronDown
                      className={cn(
                        "h-4 w-4 shrink-0 opacity-50 transition-transform duration-200",
                        showEventTypeDropdown && "rotate-180",
                      )}
                      aria-hidden
                    />
                  </button>
                  {showEventTypeDropdown && (
                    <div
                      id="opp-event-type-listbox"
                      role="listbox"
                      onScroll={handleEventTypeScroll}
                      className="absolute z-[9999] mt-1 max-h-60 w-full overflow-y-auto rounded-md border border-gray-300 bg-white shadow-xl"
                    >
                      {!eventTypesLoading && eventTypes.length === 0 ? (
                        <div className="p-3 text-sm text-muted-foreground">No event types found</div>
                      ) : (
                        <>
                          {eventTypes.map((et) => (
                            <button
                              key={et.id}
                              type="button"
                              role="option"
                              aria-selected={eventTypeId === et.id}
                              className={cn(
                                "w-full border-b border-gray-100 px-4 py-3 text-left text-sm text-gray-900 last:border-b-0 hover:bg-blue-50",
                                eventTypeId === et.id && "bg-blue-50/80",
                              )}
                              onClick={() => pickEventType(et)}
                            >
                              {et.name}
                            </button>
                          ))}
                          {eventTypesLoadingMore && (
                            <div className="flex items-center gap-2 border-t border-gray-100 px-4 py-3 text-sm text-muted-foreground">
                              <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                              Loading more…
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
                {fieldErrors.eventTypeId && (
                  <p className="mt-1 text-sm text-red-600" role="alert">
                    {fieldErrors.eventTypeId}
                  </p>
                )}
              </div>
            </>
          )}

          <section className="rounded-lg border border-gray-300 bg-white p-5">
            <h2 className="mb-4 text-lg font-semibold text-gray-900">Address</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Label htmlFor="opp-address-line1" className={labelClass}>
                  Address line 1
                </Label>
                <Input
                  id="opp-address-line1"
                  value={addressLine1}
                  onChange={(e) => setAddressLine1(e.target.value)}
                  placeholder="Street address"
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="opp-address-line2" className={labelClass}>
                  Address line 2
                </Label>
                <Input
                  id="opp-address-line2"
                  value={addressLine2}
                  onChange={(e) => setAddressLine2(e.target.value)}
                  placeholder="Suite, unit, etc."
                />
              </div>
              <div>
                <Label htmlFor="opp-city" className={labelClass}>
                  City <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="opp-city"
                  value={city}
                  onChange={(e) => {
                    setCity(e.target.value)
                    setFieldErrors((prev) => ({ ...prev, city: undefined }))
                  }}
                  placeholder="City"
                  className={cn(fieldErrors.city && "border-red-500 focus:border-red-500")}
                  aria-invalid={!!fieldErrors.city}
                />
                {fieldErrors.city && (
                  <p className="mt-1 text-sm text-red-600" role="alert">
                    {fieldErrors.city}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="opp-state" className={labelClass}>
                  State <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="opp-state"
                  value={state}
                  onChange={(e) => {
                    setState(e.target.value)
                    setFieldErrors((prev) => ({ ...prev, state: undefined }))
                  }}
                  placeholder="State"
                  className={cn(fieldErrors.state && "border-red-500 focus:border-red-500")}
                  aria-invalid={!!fieldErrors.state}
                />
                {fieldErrors.state && (
                  <p className="mt-1 text-sm text-red-600" role="alert">
                    {fieldErrors.state}
                  </p>
                )}
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="opp-zip" className={labelClass}>
                  ZIP / postal code
                </Label>
                <Input
                  id="opp-zip"
                  value={zip}
                  onChange={(e) => setZip(e.target.value)}
                  placeholder="ZIP or postal code"
                />
              </div>
            </div>
          </section>

          <div>
            <Label htmlFor="opp-thank-you" className={labelClass}>
              Source URL <span className="text-red-500">*</span>
            </Label>
            <Input
              id="opp-thank-you"
              type="url"
              inputMode="url"
              value={thankYouUrl}
              onChange={(e) => {
                setThankYouUrl(e.target.value)
                setFieldErrors((prev) => ({ ...prev, thankYouUrl: undefined }))
              }}
              placeholder="https://example.org/thank-you"
              className={cn(fieldErrors.thankYouUrl && "border-red-500 focus:border-red-500")}
              aria-invalid={!!fieldErrors.thankYouUrl}
            />
            {fieldErrors.thankYouUrl && (
              <p className="mt-1 text-sm text-red-600" role="alert">
                {fieldErrors.thankYouUrl}
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="opp-sponsor-url" className={labelClass}>
              Sponsorship opportunity URL
            </Label>
            <Input
              id="opp-sponsor-url"
              type="url"
              inputMode="url"
              value={sponsorUrl}
              onChange={(e) => {
                setSponsorUrl(e.target.value)
                setFieldErrors((prev) => ({ ...prev, sponsorUrl: undefined }))
              }}
              placeholder="Optional"
              className={cn(fieldErrors.sponsorUrl && "border-red-500 focus:border-red-500")}
              aria-invalid={!!fieldErrors.sponsorUrl}
            />
            {fieldErrors.sponsorUrl && (
              <p className="mt-1 text-sm text-red-600" role="alert">
                {fieldErrors.sponsorUrl}
              </p>
            )}
          </div>

          <div>
            <Label className={labelClass}>Sponsorship document</Label>
            <div
              className="flex h-10 w-full items-center rounded-md border border-input bg-muted px-3 text-sm text-muted-foreground"
              aria-disabled="true"
            >
              Coming soon
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating…
                </>
              ) : (
                <>
                  <Plus className="mr-2 h-4 w-4" />
                  Create opportunity
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
